import { useEffect, useRef } from "react";

import weeklyHighlightsBg from "@/assets/weekly-highlights-bg.png";
import ImagePlaceholder from "@/components/ImagePlaceholder";
import { toSafeHttpUrl } from "@/lib/media";
import type { PulseCard, WeeklyFeature } from "@/types";

const CARD_BG = ["bg-[#0DA9FF]", "bg-[#00402A]", "bg-[#FF9C1A]"] as const;
const WEEKLY_CARD_BG_HEX = ["#0DA9FF", "#00402A", "#FF9C1A"] as const;
const CARD_TITLE = ["text-[#00402A]", "text-white", "text-[#00402A]"] as const;
const PLUS_COLORS = [
  { circle: "#0E9FEE", stroke: "#003F28" },
  { circle: "#003020", stroke: "#FFFFFF" },
  { circle: "#D68010", stroke: "#003F28" },
] as const;

interface Props {
  features: WeeklyFeature[] | unknown;
  fallbackDate?: string;
  loading?: boolean;
  onSelectCard?: (card: PulseCard, sourceBg?: string) => void;
}

function toPulseCard(feature: WeeklyFeature, idx: number, fallbackDate?: string): PulseCard {
  const summary =
    feature.texto_resumen?.trim() ||
    feature.entradilla?.trim() ||
    feature.subtitulo?.trim() ||
    feature.titulo;
  const fechaPublicacion = feature.week_end || feature.week_start || fallbackDate || "1970-01-01";

  return {
    card_id: Number(`${feature.iso_year ?? 0}${feature.iso_week ?? 0}${idx}`),
    position: idx,
    title: feature.titulo,
    summary,
    source: feature.medio || "Weekly",
    url: feature.url_articulo,
    relevance_score: feature.universal_score ?? 0,
    tier: 0,
    tier_reason: "weekly-feature",
    image_url: feature.imagen_url ?? null,
    is_update: false,
    detail: {
      fecha_publicacion: fechaPublicacion,
      entradilla: feature.entradilla ?? null,
      entidades: [],
      tags: feature.tags ?? [],
      members: [],
    },
  };
}

export default function PulseWeeklyHighlights({
  features,
  fallbackDate,
  loading = false,
  onSelectCard,
}: Props) {
  const normalizedFeatures = Array.isArray(features) ? features : [];
  const cards = normalizedFeatures.slice(0, 3);
  const carouselRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = carouselRef.current;
    if (!container) return;
    if (window.innerWidth > 1024) return;

    const centerMiddleCard = () => {
      const middleCard = container.children.item(1) as HTMLElement | null;
      if (!middleCard) return;
      const target =
        middleCard.offsetLeft - (container.clientWidth - middleCard.clientWidth) / 2;
      const left = Math.max(0, target);
      if (typeof container.scrollTo === "function") {
        container.scrollTo({ left, behavior: "auto" });
      } else {
        container.scrollLeft = left;
      }
    };

    const raf = window.requestAnimationFrame(centerMiddleCard);
    window.addEventListener("resize", centerMiddleCard);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", centerMiddleCard);
    };
  }, [cards.length]);

  return (
    <section className="pulse-weekly-section mt-[var(--pulse-featured-next-to-weekly-gap)] pb-[var(--pulse-weekly-to-invite-gap)]">
      <div className="pulse-weekly-shell">
        <picture>
          <img
            src={weeklyHighlightsBg}
            alt=""
            className="pulse-weekly-bg"
            width={1688}
            height={1389.6}
            decoding="async"
          />
        </picture>

        <h2 className="pulse-weekly-title m-0">
          <span className="pulse-weekly-title-main">Destacados</span>
          <span className="pulse-weekly-title-sub">
            <span className="font-normal">del </span>
            <span className="font-bold">Mes</span>
          </span>
        </h2>

        <div className="pulse-weekly-carousel">
          <div ref={carouselRef} className="pulse-weekly-cards">
          {Array.from({ length: 3 }).map((_, idx) => {
            const feature = cards[idx];
            const imageUrl = toSafeHttpUrl(feature?.imagen_url ?? null);
            const canOpen = !!feature;

            const onOpen = () => {
              if (!feature || !onSelectCard) return;
              onSelectCard(
                toPulseCard(feature, idx, fallbackDate),
                WEEKLY_CARD_BG_HEX[idx] ?? WEEKLY_CARD_BG_HEX[0],
              );
            };

            return (
              <article
                key={feature?.url_articulo ?? `weekly-${idx}`}
                role={canOpen ? "button" : undefined}
                tabIndex={canOpen ? 0 : -1}
                aria-label={feature?.titulo ?? `Destacada semanal ${idx + 1}`}
                onClick={canOpen ? onOpen : undefined}
                onKeyDown={
                  canOpen
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onOpen();
                        }
                      }
                    : undefined
                }
                className={`pulse-weekly-card group relative ${canOpen ? "cursor-pointer" : ""} ${CARD_BG[idx]} ${loading ? "is-pulse-loading" : ""}`}
              >
                {imageUrl ? (
                  <div className="pulse-weekly-media pulse-card-media overflow-hidden">
                    <img
                      src={imageUrl}
                      alt=""
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                    />
                  </div>
                ) : (
                  !loading && (
                    <ImagePlaceholder
                      src={null}
                      className="pulse-weekly-media overflow-hidden opacity-35"
                    />
                  )
                )}

                <h3 className={`pulse-weekly-card-title m-0 ${CARD_TITLE[idx]}`}>
                  {feature?.titulo ?? "Cargando destacada semanal..."}
                </h3>

                <button
                  type="button"
                  aria-label="Ver detalle"
                  className="pulse-plus-btn pulse-weekly-plus absolute z-20 flex h-[56px] w-[56px] items-center justify-center overflow-hidden rounded-full transition hover:scale-105"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen();
                  }}
                  disabled={!canOpen}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="56"
                    height="56"
                    viewBox="0 0 56 56"
                    fill="none"
                    aria-hidden
                  >
                    <circle cx="28" cy="28" r="28" fill={PLUS_COLORS[idx].circle} />
                    <path
                      d="M27.9 17.8v20.4M38.2 28H17.8"
                      stroke={PLUS_COLORS[idx].stroke}
                      strokeWidth="2"
                    />
                  </svg>
                </button>
              </article>
            );
          })}
          </div>
        </div>
      </div>
    </section>
  );
}
